import { Injectable, Injector } from '@angular/core';
import { Database, get, onValue, off, ref, DatabaseReference } from '@angular/fire/database';
import { APP_CONFIG, GLOBAL_DATA_KEY, LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs';
import { TopicFirebase } from '@core/constants';
import { AuthService } from '@core/services/apis/auth.service';
import { SessionStorageService } from '../session-storage.service';
import { GlobalDataService } from '../global-data.service';
import { LocalStorageService } from '../local-storage.service';
import { FirebaseAuthService } from './firebase-auth.service';

/** Maximum retries when a version node returns PERMISSION_DENIED. */
const MAX_PERMISSION_RETRY = 3;

/** Baseline value stored in sessionStorage when the version node doesn't exist yet on Firebase. */
const VERSION_BASELINE = '1';

/**
 * Manages the "version" nodes on Firebase — fully independent of the connection/data-sync flow.
 *
 *  - App version nodes (web/tv): app-level, no login required, live for the whole app lifecycle.
 *      → reload the page when the web app version changes / notify AndroidBridge when the tv app version changes.
 *  - Force topics (forceLogout / forceSyncMaster): per office, registered after connecting.
 */
@Injectable({
  providedIn: 'root'
})
export class FirebaseVersionService {

  // App-level listeners — separate ref per topic to avoid orphaned listeners on re-subscribe.
  private webAppVersionRef: DatabaseReference | null = null;
  private tvAppVersionRef: DatabaseReference | null = null;

  // Office-level listeners.
  private forceLogoutRef: DatabaseReference | null = null;
  private forceSyncRef: DatabaseReference | null = null;

  /** True while both office-level force-topic listeners are registered. */
  public get hasForceTopicListeners(): boolean {
    return !!this.forceLogoutRef && !!this.forceSyncRef;
  }

  constructor(
    private db: Database,
    private sessionStorage: SessionStorageService,
    private firebaseAuth: FirebaseAuthService,
    private globalData: GlobalDataService,
    private localStorage: LocalStorageService,
    private injector: Injector
  ) {}

  // ───────────────────────────── APP VERSION NODES ─────────────────────────────

  /** Listen to one app version node (web or tv). Detaches the old ref before re-registering. */
  public watchAppVersionNode(topic: string, retryCount = 0) {
    if (!APP_CONFIG.useFirebase) return;

    const isWebTopic = topic === TopicFirebase.WEB_APP_VERSION;

    // Use a separate ref per topic and always off() before re-subscribing
    // to avoid orphaned listeners / overlapping callbacks on retry.
    if (isWebTopic) {
      if (this.webAppVersionRef) off(this.webAppVersionRef);
      this.webAppVersionRef = ref(this.db, topic);
    } else {
      if (this.tvAppVersionRef) off(this.tvAppVersionRef);
      this.tvAppVersionRef = ref(this.db, topic);
    }

    const versionNodeRef = isWebTopic ? this.webAppVersionRef! : this.tvAppVersionRef!;

    onValue(
      versionNodeRef,
      (snapshot) => {
        if (isWebTopic) {
          this.onWebAppVersionChanged(snapshot);
        } else {
          this.onTvAppVersionChanged(snapshot);
        }
      },
      (error) => this.retryOnPermissionDenied(error, topic, retryCount)
    );
  }

  private onTvAppVersionChanged(snapshot: any) {
    const androidBridge = (window as any).AndroidBridge;
    if (androidBridge && typeof androidBridge.onVersionTVChange === 'function') {
      androidBridge.onVersionTVChange(snapshot.val());
    }
  }

  private onWebAppVersionChanged(snapshot: any) {
    const latestVersion = snapshot.val();
    const knownVersion = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.WEB_APP_VERSION);

    // First time seeing the node → just record it, don't reload.
    if (!knownVersion) {
      this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.WEB_APP_VERSION, latestVersion);
      return;
    }

    // Version unchanged → skip.
    if (knownVersion == latestVersion) return;

    // A new web app version was deployed → reload to load the new code.
    this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.WEB_APP_VERSION, latestVersion);
    if (this.isUserLoggedInFirebase()) {
      // Logged in: refresh the token before reloading so the new page starts with clean claims.
      this.firebaseAuth.fireAuthRefreshToken().finally(() => window.location.reload());
    } else {
      // Not logged in: no token to refresh, reload immediately.
      window.location.reload();
    }
  }

  // ──────────────────────────── FORCE TOPICS (logout / sync) ───────────────────

  /** Register listeners for force-logout & force-sync per office. */
  public watchForceTopics(corpPrefix: string, officeCd: string) {
    if (!officeCd || !corpPrefix) return;

    // Detach old listeners before re-registering to avoid duplicates.
    this.unwatchForceTopics();

    this.forceLogoutRef = ref(this.db, `d/${corpPrefix}/${officeCd}/v/l`);
    onValue(this.forceLogoutRef, (snapshot) => this.onForceLogoutVersionChanged(snapshot));

    this.forceSyncRef = ref(this.db, `d/${corpPrefix}/${officeCd}/v/m`);
    onValue(this.forceSyncRef, (snapshot) => this.onForceSyncVersionChanged(snapshot));
  }

  /** Stop listening to force-logout & force-sync. */
  public unwatchForceTopics() {
    if (this.forceLogoutRef) {
      off(this.forceLogoutRef);
      this.forceLogoutRef = null;
    }
    if (this.forceSyncRef) {
      off(this.forceSyncRef);
      this.forceSyncRef = null;
    }
  }

  private onForceLogoutVersionChanged(snapshot: any) {
    const knownVersion = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.FORCE_LOGOUT_VERSION);
    const latestVersion = snapshot.val();

    if (!knownVersion) {
      // Node doesn't exist yet (latestVersion = null) → store baseline "1" as the marker,
      // so that next time Caremoni-Console creates the node with a different version it triggers logout.
      const versionToSave = latestVersion ?? VERSION_BASELINE;
      this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.FORCE_LOGOUT_VERSION, versionToSave);
      this.globalData.set(GLOBAL_DATA_KEY.officeVersion, versionToSave);
    } else if (latestVersion !== null && knownVersion != latestVersion) {
      this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.FORCE_LOGOUT_VERSION, latestVersion);
      this.globalData.set(GLOBAL_DATA_KEY.officeVersion, latestVersion);
      this.handleForceLogout(latestVersion);
    }
  }

  private onForceSyncVersionChanged(snapshot: any) {
    const knownVersion = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.FORCE_SYNC_MASTER_VERSION);
    const latestVersion = snapshot.val();

    if (!knownVersion) {
      // Node doesn't exist yet (latestVersion = null) → store baseline "1" as the marker,
      // so that next time Caremoni-Console creates the node with a different version it triggers sync.
      const versionToSave = latestVersion ?? VERSION_BASELINE;
      this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.FORCE_SYNC_MASTER_VERSION, versionToSave);
    } else if (latestVersion !== null && knownVersion != latestVersion) {
      // Currently only records the new version, does NOT auto sync/alert (per current business rule).
      this.sessionStorage.setSessionData(SESSION_STORAGE_KEY.FORCE_SYNC_MASTER_VERSION, latestVersion);
    }
  }

  private handleForceLogout(version: string) {
    console.log('Force logout triggered by version change:', version);
    // setTimeout so we don't block the Firebase listener callback.
    setTimeout(() => this.performForceLogout(), 100);
  }

  private async performForceLogout() {
    try {
      const authService = this.injector.get(AuthService);
      await authService.logout();
    } catch (error) {
      console.error('Error during force logout:', error);
      // Fallback: clear the session and navigate to the login page.
      this.sessionStorage.clearSession();
      window.location.href = `/auth`;
    }
  }

  // ─────────────────────────────── INTERNAL ───────────────────────────────────

  /** PERMISSION_DENIED on an app version node → refresh the token then re-register. */
  private async retryOnPermissionDenied(error: any, topic: string, retryCount: number) {
    if (error.code !== 'PERMISSION_DENIED' || retryCount >= MAX_PERMISSION_RETRY) return;

    const refreshed = await this.firebaseAuth.fireAuthRefreshToken();
    if (refreshed) {
      this.watchAppVersionNode(topic, retryCount + 1);
    }
  }

  /** Whether the user is logged in (office context + Firebase enabled) — pure boolean, no side effects. */
  private isUserLoggedInFirebase(): boolean {
    const corpPrefix = this.localStorage.getItem(LOCAL_STORAGE_KEY.corpPrefix) || '';
    const officeSelected = this.sessionStorage.getSessionData(SESSION_STORAGE_KEY.officeSelected);
    return !!(corpPrefix && officeSelected && APP_CONFIG.useFirebase);
  }

  /** Clean up app-level version listeners (called when the facade is destroyed). */
  public destroy() {
    if (this.webAppVersionRef) { off(this.webAppVersionRef); this.webAppVersionRef = null; }
    if (this.tvAppVersionRef) { off(this.tvAppVersionRef); this.tvAppVersionRef = null; }
  }

  /** Requests an Android TV app update using the current Firebase TV-app version. */
  public async requestTvAppUpdate(): Promise<boolean> {
    const androidBridge = (window as any).AndroidBridge;
    if (!androidBridge || typeof androidBridge.onVersionTVChange !== 'function') {
      return false;
    }

    const versionRef = this.tvAppVersionRef ?? ref(this.db, TopicFirebase.TV_APP_VERSION);
    const versionSnapshot = await get(versionRef);
    androidBridge.onVersionTVChange(versionSnapshot.val());
    return true;
  }
}
